/**
 * Experience and levels (CADRAGE: "Courbe d'XP exponentielle douce"; pacing
 * in `docs/adr/0031-pacing.md`).
 *
 * Levels are derived from total experience, never stored on their own as the
 * truth: `levelForXp` is the single definition, so a saved character and a
 * live one can never disagree about what level a given amount of XP is.
 */
/**
 * The last level with creatures to match (the yeti, the snow's band top):
 * 30 in the CADRAGE, back to 30 once a band of content stands past 20.
 */
export const MAX_LEVEL = 20;
/**
 * Tuning knobs: `xpToNextLevel(level) = FIRST_STEP + GROWTH * (level - 1) ^ EXPONENT`.
 * Set so that a biome lasts about as long as gearing up in it (ADR 0031): an
 * hour or less for the meadow, then two to ten hours a band, some twenty-five
 * to the cap for a steady player. The additive first step keeps the ratio
 * between consecutive steps under two everywhere; the exponent makes the top slow.
 */
const FIRST_STEP = 100;
const GROWTH = 35;
const EXPONENT = 2.25;
/** XP needed to go from `level` to `level + 1`. A rat (4 XP) pays for level 2 in twenty-five kills. */
export function xpToNextLevel(level) {
    if (level < 1)
        throw new RangeError(`Level must be at least 1, got ${String(level)}`);
    return Math.round(FIRST_STEP + GROWTH * (level - 1) ** EXPONENT);
}
/** Total XP at which `level` is reached. Level 1 is free. */
export function xpThreshold(level) {
    if (level < 1)
        throw new RangeError(`Level must be at least 1, got ${String(level)}`);
    let total = 0;
    for (let current = 1; current < level; current += 1)
        total += xpToNextLevel(current);
    return total;
}
/** The level a character with `xp` total experience has. Capped at MAX_LEVEL. */
export function levelForXp(xp) {
    let level = 1;
    while (level < MAX_LEVEL && xp >= xpThreshold(level + 1))
        level += 1;
    return level;
}
/**
 * A creature this many levels under the player still pays in full; each level
 * further takes `XP_FALLOFF_PER_LEVEL` of it, down to `XP_FLOOR_SHARE`. Only
 * weaker creatures are cut: a stronger one is its own risk, and its own reward.
 */
export const XP_FULL_GAP = 2;
export const XP_FALLOFF_PER_LEVEL = 0.25;
export const XP_FLOOR_SHARE = 0.1;
/** Share of a creature's experience a player of `playerLevel` earns, in (0, 1]. */
export function xpShare(playerLevel, mobLevel) {
    if (mobLevel === undefined)
        return 1;
    const below = playerLevel - mobLevel - XP_FULL_GAP;
    if (below <= 0)
        return 1;
    return Math.max(XP_FLOOR_SHARE, 1 - XP_FALLOFF_PER_LEVEL * below);
}
/** Experience a kill is worth to this player, a point at least for a creature worth anything. */
export function killXp(xp, playerLevel, mobLevel) {
    if (xp <= 0)
        return 0;
    return Math.max(1, Math.round(xp * xpShare(playerLevel, mobLevel)));
}
/**
 * Add experience. Total XP keeps growing at the cap (a future raise of
 * MAX_LEVEL then applies retroactively), but the level does not.
 */
export function gainXp(progress, amount) {
    const gained = Math.max(0, Math.floor(amount));
    const xp = progress.xp + gained;
    const level = levelForXp(xp);
    return { xp, level, levelsGained: Math.max(0, level - progress.level) };
}
/** Progress within the current level, for a bar: 0 at the level's start, 1 at the next. */
export function levelFraction(progress) {
    if (progress.level >= MAX_LEVEL)
        return 1;
    const start = xpThreshold(progress.level);
    const span = xpToNextLevel(progress.level);
    return Math.min(1, Math.max(0, (progress.xp - start) / span));
}
//# sourceMappingURL=progression.js.map